import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { installHttpCache } from "@/lib/httpCache";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { BrandingProvider } from "@/contexts/BrandingContext";
import { Toaster } from "@/components/ui/sonner";
import LoginPage from "@/pages/LoginPage";
import ChangePasswordPage from "@/pages/ChangePasswordPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import SuperadminPage from "@/pages/SuperadminPage";
import SignupPage from "@/pages/SignupPage";
import PaymentSuccessPage from "@/pages/PaymentSuccessPage";
import PaymentCancelPage from "@/pages/PaymentCancelPage";
import LegalPage from "@/pages/LegalPage";
import ManualPage from "@/pages/ManualPage";
import FattureInCloudLanding from "@/pages/FattureInCloudLanding";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import ClientDashboard from "@/pages/client/ClientDashboard";
import { Loader2 } from "lucide-react";

installHttpCache();

function LoadingScreen() {
    return (
        <div className="min-h-screen flex items-center justify-center bg-[#F2F5F2]">
            <Loader2 className="h-8 w-8 animate-spin text-brand" />
        </div>
    );
}

function ProtectedRoute({ children, allowedRoles }) {
    const { user, loading } = useAuth();

    if (loading || user === undefined) return <LoadingScreen />;
    if (user === null) return <Navigate to="/login" replace />;
    if (user.must_change_password) return <Navigate to="/change-password" replace />;
    if (allowedRoles && !allowedRoles.includes(user.role)) {
        return <Navigate to={user.role === 'admin' ? '/admin' : '/client'} replace />;
    }
    return children;
}

function PublicRoute({ children }) {
    const { user, loading } = useAuth();

    if (loading || user === undefined) return <LoadingScreen />;
    if (user && user !== null && !user.must_change_password) {
        return <Navigate to={user.role === 'admin' ? '/admin' : '/client'} replace />;
    }
    return children;
}

function AppRoutes() {
    return (
        <Routes>
            <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/superadmin" element={<SuperadminPage />} />
            <Route path="/signup" element={<PublicRoute><SignupPage /></PublicRoute>} />
            <Route path="/payment/success" element={<PaymentSuccessPage />} />
            <Route path="/payment/cancel" element={<PaymentCancelPage />} />
            <Route path="/terms" element={<LegalPage />} />
            <Route path="/privacy" element={<LegalPage />} />
            <Route path="/dpa" element={<LegalPage />} />
            <Route path="/sub-responsabili" element={<LegalPage />} />
            <Route path="/manuale" element={<ManualPage />} />
            <Route path="/integrazione-fatture-in-cloud" element={<FattureInCloudLanding />} />
            <Route path="/change-password" element={<ChangePasswordPage />} />
            <Route path="/admin/*" element={<ProtectedRoute allowedRoles={['admin']}><AdminDashboard /></ProtectedRoute>} />
            <Route path="/client/*" element={<ProtectedRoute allowedRoles={['client']}><ClientDashboard /></ProtectedRoute>} />
            <Route path="/" element={<Navigate to="/login" replace />} />
            <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
    );
}

function App() {
    return (
        <div className="App">
            <BrowserRouter>
                <AuthProvider>
                    <BrandingProvider>
                        <AppRoutes />
                        <Toaster position="top-right" richColors />
                    </BrandingProvider>
                </AuthProvider>
            </BrowserRouter>
        </div>
    );
}

export default App;
